import { Accessor, createSignal, JSX, onCleanup } from 'solid-js';
import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
  Placement,
} from '@floating-ui/dom';
import {
  type ControlDropdownPosition,
  resolveControlDropdownPlacement,
} from './resolveDropdownHorizontalPlacement';
import { alignDropdownMenuContentWithTrigger } from './alignDropdownMenuContentWithTrigger';

const MENU_OFFSET = 8;

export interface UseFloatingMenuPositionOptions {
  getPosition?(): ControlDropdownPosition | undefined;
  /** Raw placement, for callers that pick a side themselves. Wins over getPosition. */
  getPlacement?(): Placement | undefined;
  getAnchor(): HTMLElement | undefined;
}

export interface UseFloatingMenuPositionResult {
  readonly menuStyle: Accessor<JSX.CSSProperties>;
  readonly opensUpward: Accessor<boolean>;
  /** Ref callback for the floating menu section; positions it, then tracks the trigger. */
  setMenuSectionRef(el: HTMLElement | null, onPositioned?: () => void): void;
}

/**
 * @intent Owns floating-ui positioning for a dropdown menu: seeds an initial
 * anchor-aligned position on the next frame once menu content has rendered, then
 * keeps following the trigger for as long as the menu stays open. Shared by
 * ControlDropdown and SplitControlDropdown.
 */
export const useFloatingMenuPosition = (
  options: UseFloatingMenuPositionOptions
): UseFloatingMenuPositionResult => {
  const [menuStyle, setMenuStyle] = createSignal<JSX.CSSProperties>({
    visibility: 'hidden',
  });
  const [opensUpward, setOpensUpward] = createSignal(false);
  let menuSectionRef: HTMLElement | undefined;
  let stopTrackingAnchor: (() => void) | undefined;

  const stopTracking = (): void => {
    stopTrackingAnchor?.();
    stopTrackingAnchor = undefined;
  };

  onCleanup(stopTracking);

  const resolvePlacement = (): Placement =>
    options.getPlacement?.() ??
    resolveControlDropdownPlacement(options.getPosition?.());

  const updatePosition = async (): Promise<void> => {
    const anchorRef = options.getAnchor();
    const menu = menuSectionRef;
    if (!anchorRef || !menu) return;
    // The menu is unmounted on close while tracking may still have a pending frame.
    if (!menu.isConnected) {
      stopTracking();
      return;
    }
    const { x, y, placement } = await computePosition(anchorRef, menu, {
      placement: resolvePlacement(),
      middleware: [
        offset(MENU_OFFSET),
        flip(),
        shift({ crossAxis: false, padding: MENU_OFFSET }),
      ],
    });

    if (menuSectionRef !== menu || !menu.isConnected) return;

    const aligned = alignDropdownMenuContentWithTrigger({
      anchor: anchorRef,
      menu,
      x,
      y,
      placement,
    });

    setMenuStyle({
      top: `${aligned.y}px`,
      left: `${aligned.x}px`,
      visibility: 'visible',
    });
    setOpensUpward(placement.startsWith('top'));
  };

  /**
   * Opening the dropdown reflows the toolbar around the trigger, so the trigger
   * can still drift a couple of pixels after the first measurement. Follow it
   * rather than trusting one reading, which also keeps the menu aligned while the
   * pane is resized or scrolled.
   */
  const trackAnchor = (): void => {
    const anchorRef = options.getAnchor();
    if (!anchorRef || !menuSectionRef?.isConnected) return;
    stopTrackingAnchor = autoUpdate(anchorRef, menuSectionRef, () => {
      void updatePosition();
    });
  };

  const setMenuSectionRef = (
    el: HTMLElement | null,
    onPositioned?: () => void
  ): void => {
    stopTracking();
    menuSectionRef = el ?? undefined;
    if (!el) return;

    // Solid never passes null to a ref, so teardown has to hang off the owner
    // that created the menu, which disposes as soon as the dropdown closes.
    onCleanup(() => {
      if (menuSectionRef === el) menuSectionRef = undefined;
      stopTracking();
    });

    // Wait for next frame so ControlMenu content has rendered, then position
    requestAnimationFrame(() => {
      if (menuSectionRef !== el) return;
      void updatePosition().then(() => {
        if (menuSectionRef !== el) return;
        onPositioned?.();
        trackAnchor();
      });
    });
  };

  return { menuStyle, opensUpward, setMenuSectionRef };
};
